import { faDownload, faFile, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FileRejection } from "react-dropzone";
import { Link, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { EDiaryConfiguration, EDiaryConfigurationFile } from "../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import DocumentTitle from "../../misc/components/DocumentTitle";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DragDrop } from "../../misc/DragDrop";
import { FileDownload } from "../../misc/FileDownload";
import "./FileManagerStyles.css";
import { FileItem } from "./DiaryConfigurationFileItem";

export interface FileAndName {
    id: string;
    file: File,
    friendlyName: string | undefined;
    isEditing: boolean;
}

export interface EDiaryConfigurationFileEditing extends EDiaryConfigurationFile {
    isEditing: boolean;
}

export const DiaryConfigurationFiles = () => {
    const { configId } = useParams<{ configId: string }>();

    const [alert, setAlert] = React.useState<string>();
    const [config, setConfig] = React.useState<EDiaryConfiguration>();
    const [filesToUpload, setFilesToUpload] = React.useState<FileAndName[]>([]);
    const [filesToEdit, setFilesToEdit] = React.useState<EDiaryConfigurationFileEditing[]>([]);
    const [uploading, setUploading] = React.useState<boolean>(false);
    const [complete, setComplete] = React.useState<boolean>(false);
    const [deleting, setDeleting] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
        setComplete(false);
        setAlert(undefined);

        if (rejected.length > 0) {
            setAlert(
                `${rejected[0].file.name} - ${rejected[0].errors.map(e => e.message).join(", ")}`
            );
            return;
        }

        setFilesToUpload([
            ...filesToUpload,
            ...accepted.map(a => ({ file: a, friendlyName: null, isEditing: false, id: v4() }))
        ]);
    };

    const getConfig = async () => {
        const result = await legacyClient.getEDiaryConfigurations({
            Filters: [{ Member: "Id", Values: [configId] }]
        });

        setConfig(result.Data[0]);
        setFilesToEdit(result.Data[0].Files.map(x => ({ ...x, isEditing: false })));
    };

    React.useEffect(
        () => {
            getConfig();
        },
        [configId]
    );

    if (!config) return <LoadingWithMessage message="Loading Configuration Files" />;

    const onDeleteFile = async (id: string) => {
        try {
            setDeleting(true);
            const response = await legacyClient.deleteEDiaryConfigurationFile(id);
            if (!response.ok)
                return setAlert((await response.json()).Errors[0].Message);
            await getConfig();
        } catch (ex) {
            setAlert(ex.Message);
        } finally {
            setDeleting(false);
        }
    };

    const onDownloadFile = async (file: EDiaryConfigurationFile) => {
        const responseFile = await legacyClient.downloadEDiaryConfigurationFile(file.Id);
        FileDownload(responseFile, file.Name);
    };

    const onSave = async () => {
        try {
            setComplete(false);
            setUploading(true);
            if (filesToUpload.length > 0)
                await legacyClient.uploadEDiaryConfigurationFiles(configId, filesToUpload);

            for (let file of filesToEdit) {
                await client.client.patchDiaryConfigurationFile({
                    friendlyName: file.FriendlyName,
                }, file.Id, "");
            }
            setComplete(true);

            setFilesToUpload([]);
            setFilesToEdit([]);
            await getConfig();
            setUploading(false);
        } catch (ex) {
            setAlert(ex.Message);
            setUploading(false);
        }       
    }

    

    const CurrentFileItems = () => {
        return <>{filesToEdit.map((file) => (
            <FileItem key={`File_${file.Name}_${file.Id}`}
                id={file.Id}
                fileName={file.Name}
                friendlyName={file.FriendlyName}
                created={file.CreatedDateTime}
                isDeleting={deleting}
                isEditing={file.isEditing}
                setEditing={() => onSetEditingCurrent(file.Name)}
                onDownload={() => onDownloadFile(file)}
                onDelete={() => onDeleteFile(file.Id)}
                onFriendlyNameChange={(fn, friendly) => onFriendlyNameChange(fn, friendly)}
            />
        ))}</>;
    }

    const onPendingDelete = (file: File) => {
        const remainingFiles = filesToUpload.filter(x => x.file.name !== file.name);
        setFilesToUpload(remainingFiles);
    }

    const onFriendlyNameChange = (existingFileName: string, friendlyName: string) => {
        let fileToUpdate = filesToUpload.find(x => x.file.name === existingFileName);
        if (fileToUpdate) {

            setFilesToUpload(prevFiles =>
                prevFiles.map(file =>
                    file.file.name === existingFileName
                        ? { ...file, friendlyName: friendlyName }
                        : file
                )
            );
            return;
        }
        else {
            setFilesToEdit(prevFiles =>
                prevFiles.map(file =>
                    file.Name === existingFileName
                        ? { ...file, FriendlyName: friendlyName }
                        : file
                )
            );
            return;
        }       
    }

    const onSetEditingCurrent = (existingFileName: string) => {
        setFilesToEdit(prevFiles =>
            prevFiles.map(file =>
                file.Name === existingFileName
                    ? { ...file, isEditing: !file.isEditing }
                    : file
            )
        );
    }

    const onSetEditingPending = (existingFileName: string) => {
        setFilesToUpload(prevFiles =>
            prevFiles.map(file =>
                file.file.name === existingFileName
                    ? { ...file, isEditing: !file.isEditing }
                    : file
            )
        );
    }

    const PendingFileItems = () => {
        return <>{filesToUpload.map(f => {
            return (<FileItem key={`PendingFile_${f.file.name}_${f.id}`}
                id={f.id}
                fileName={f.file.name}
                friendlyName={f.friendlyName}
                isEditing={f.isEditing}
                setEditing={() => onSetEditingPending(f.file.name)}
                isDeleting={deleting}
                onDelete={() => onPendingDelete(f.file)}
                onFriendlyNameChange={(fn, friendlyName) => onFriendlyNameChange(f.file.name, friendlyName)}
            />);
        })}</>;
    }

    return (
        <DocumentTitle title="Diary Configuration Files | Vitalograph Web Portal">
            <Alert show={alert !== undefined} category={AlertCategoryEnum.Danger}>
                {alert}
            </Alert>
            <Alert show={complete} category={AlertCategoryEnum.Success}>
                Files saved successfully
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Diary Configuration Files</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Link
                            className="btn btn-primary btn-outline"
                            to="/DiaryConfigurations"
                            data-automationid="DiaryConfigurationFiles_ReturnToList"
                        >
                            Return to List
                        </Link>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <DragDrop
                                id="FilesDragDrop"
                                options={{
                                    onDrop,
                                    accept: { "text/xml": [".xml"] }
                                }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12" style={{
                            display: "flex",
                            flexWrap: "wrap",
                            marginTop: "20px",
                            gap: "10px",
                        }}>
                            {CurrentFileItems()}
                            {PendingFileItems()}
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12 pull-right">
                    <AsyncActionButton className="btn btn-primary" isSubmitting={uploading} onClick={() => onSave()}>Save</AsyncActionButton>
                </div>
            </div>
        </DocumentTitle>
    );
};
